import React from "react";
import Head from "next/head";

import useDomainCountryCode from "hooks/useDomainCountryCode";
import { withRedux } from "redux/wrapper";

// These values are included in the Sailthru tags
const SAILTHRU_LOCALES: { [key: string]: string } = {
  CA: "en-ca",
  US: "en-us",
};

type Props = {
  description: string;
  tags?: string[];
  title: string;
  imageUrl?: string;
};

export const SailthruMetaTags: React.FC<Props> = withRedux(
  ({ description, imageUrl, tags = [], title }: Props) => {
    const countryCode = useDomainCountryCode();
    const locale = SAILTHRU_LOCALES[countryCode] || "en-us";

    /**
     * Sailthru Tags are formatted as a comma-separated list of tags, which are
     * all lowercase and removed of non-letter and non-number characters. These
     * tags provide metadata for targeting Sailthru campaigns.
     *
     * Read more: https://getstarted.sailthru.com/site/personalization-engine/meta-tags/#sailthrutags_keywords
     */
    const sailthruTags = [locale, ...tags]
      .map(
        (tag) =>
          tag
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "-") // spaces to '-'
            .replace(/[^a-z\d-]/g, "") // drop all chars not letters numbers or '-'
      )
      .join(",");

    return (
      <Head>
        <meta name="sailthru.description" content={description} />
        {imageUrl && (
          <>
            <meta
              name="sailthru.image.full"
              content={`${imageUrl}?w=1200&fm=jpg`}
            />
            <meta
              name="sailthru.image.thumb"
              content={`${imageUrl}?w=84&fm=jpg`}
            />
          </>
        )}
        <meta name="sailthru.tags" content={sailthruTags} />
        <meta name="sailthru.title" content={title} />
      </Head>
    );
  }
);
